import { issuesTypesStateSelector } from 'core/redux/selectors/issues';
import React, { FC, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useI18n } from '../../../../i18n';
import { useDispatch, useSelector } from 'react-redux';
import {
  devicesSelector,
  digestSelector,
  resolutionTypesSelector,
  runwaysSelector,
  widgetsSelector,
} from '../../../redux/selectors/registry';
import {
  CardCounter,
  CardIndicator,
  ClosedRunwayCard,
  DeviceListItem,
  DeviceListName,
  DevicesList,
  OpenedRunwayCard,
  RunwayCards,
  RunwaysListStyled,
} from '../../../../ui/widgets/RunwayInfo.styled';
import { actions } from 'core/redux/api/veta/runwayGuardDigest';
import { actions as runwayActions } from 'core/redux/api/veta/runways';
import NoDataView from '../../general/view/NoDataView';
import { IconSize } from '../../../img';
import DeviceAssetTypeIcon from '../../share/devices/DeviceAssetTypeIcon';
import { Select } from '../../../../components/molecules/select';
import { ISelectOption } from '../../../../types/select';
import { useHistory } from 'react-router-dom';
import { AllRoutes } from '../../../../constants/routes';
import { ResolutionKind, RuleGroup } from '../../../../constants/issues';
import { actions as widgetsActions } from '../../../redux/app/widgets';

interface IRunwayInfoProps {
  uid: any;
  reduxKey: any;
  expanded: boolean;
  customRef: React.RefObject<any>;
  update(): void;
}

export enum DeviceIssueMnemonics {
  UnapprovedVehicleNearRunway = 'unapproved-vehicle-near-runway',
  UnapprovedVehicleInRunway = 'unapproved-vehicle-in-runway',
  UnauthorizedEntry = 'unauthorized-entry',
  UnauthorizedExit = 'unauthorized-exit',
  VehicleInTaxiway = 'vehicle-in-taxiway',
  VehicleNearRunway = 'vehicle-near-runway',
  VehicleInRunway = 'vehicle-in-runway',
  UnauthorizedVehicleApproachingRunway = 'unauthorized-vehicle-approaching-runway',
  UnapprovedVehicleApproachingRunway = 'unapproved-vehicle-approaching-runway',
  VehicleApproachingRunway = 'vehicle-approaching-runway',
  UnauthorizedVehicleNearRunway = 'unauthorized-vehicle-near-runway',
  UnauthorizedVehicleInRunway = 'unauthorized-vehicle-in-runway',
}

export enum BadgeTypes {
  Unknown = 0,
  NearOpenedRunwayGreen = 1,
  NearOpenedRunwayOrange = 2,
  InTaxiwayRed = 9,
  InTaxiwayGreen = 10,
  InOpenedRunwayGreen = 3,
  InOpenedRunwayOrange = 4,
  InOpenedRunwayRed = 5,
  InClosedRunwayGreen = 6,
  InClosedRunwayOrange = 7,
  InClosedRunwayRed = 8,
  WarningGreen = 30,
  AlertGreen = 31,
  AlertOrange = 32,
  SevereGreen = 33,
  SevereRed = 34,
  WarningGreenRWY = 35,
  AlertGreenRWY = 36,
  AlertOrangeRWY = 37,
  SevereGreenRWY = 38,
  SevereRedRWY = 39,
}

const RunwayInfo: FC<IRunwayInfoProps> = (props) => {
  const { f, fc } = useI18n();
  const [selectedRunway, setSelectedRunway] = useState<ISelectOption<number> | null>(null);
  const runways = useSelector(runwaysSelector);
  const digest = useSelector(digestSelector);
  const devices = useSelector(devicesSelector);
  const resolutionTypes = useSelector(resolutionTypesSelector);
  const widgets = useSelector(widgetsSelector);
  const dispatch = useDispatch();
  const history = useHistory();
  const issueTypes = useSelector(issuesTypesStateSelector);
  const issueTypesMap = new Map<number, string>();

  if (issueTypes?.list?.length && issueTypes?.list?.length > 0) {
    for (const issueType of issueTypes?.list) {
      issueTypesMap.set(issueType.id, issueType.mnemonics);
    }
  }

  const getDeviceListItem = (device: { uri: string; issue: { issueTypeId: number; resolutionTypeId: number } }) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    const badgeType = getBadgeType(
      device,
      resolutionTypes?.map ? resolutionTypes?.map[device.issue.resolutionTypeId]?.resolutionKind : null,
    );
    const foundDevice = (devices && devices.list?.find((d) => d.uri === device.uri)) || null;
    return foundDevice ? (
      <DeviceListItem key={device?.uri}>
        <DeviceListName>
          <DeviceAssetTypeIcon
            size={IconSize.MD}
            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
            // @ts-ignore
            categoryIds={foundDevice.categoryIds}
          />
          <div className="device-name">{foundDevice.denomination()}</div>
        </DeviceListName>
        {selectedRunway && digest.digests[selectedRunway.value].runwayOpen && (
          <CardIndicator color={getColorByIssueType(badgeType) || ''}>{getBadgeText(badgeType)}</CardIndicator>
        )}
      </DeviceListItem>
    ) : null;
  };

  const getBadgeText = (badgeType: BadgeTypes | undefined) => {
    if (
      badgeType === BadgeTypes.WarningGreenRWY ||
      badgeType === BadgeTypes.AlertGreenRWY ||
      badgeType === BadgeTypes.SevereGreenRWY ||
      badgeType === BadgeTypes.AlertOrangeRWY ||
      badgeType === BadgeTypes.SevereRedRWY
    ) {
      return 'RWY';
    }

    return '';
  };

  const getBadgeType = (device: { uri?: string; issue: any }, kind: ResolutionKind | null) => {
    const issueTypeMnemonic = issueTypesMap.get(device.issue.issueTypeId);
    const isRWYVehicle =
      issueTypeMnemonic === DeviceIssueMnemonics.VehicleInRunway ||
      issueTypeMnemonic === DeviceIssueMnemonics.UnapprovedVehicleInRunway ||
      issueTypeMnemonic === DeviceIssueMnemonics.UnauthorizedVehicleInRunway;

    if (device.issue?.severity === 'NOTICE') {
      if (isRWYVehicle) {
        return BadgeTypes.WarningGreenRWY;
      }

      return BadgeTypes.WarningGreen;
    }
    if (device.issue?.severity === 'WARNING') {
      if (kind === ResolutionKind.AuthorizedSituation || kind === ResolutionKind.FalsePositive) {
        if (isRWYVehicle) {
          return BadgeTypes.AlertGreenRWY;
        }
        return BadgeTypes.AlertGreen;
      }
      if (isRWYVehicle) {
        return BadgeTypes.AlertOrangeRWY;
      }
      return BadgeTypes.AlertOrange;
    }
    if (device.issue?.severity === 'SEVERE' || device.issue?.severity === 'ALERT') {
      if (kind === ResolutionKind.AuthorizedSituation || kind === ResolutionKind.FalsePositive) {
        if (isRWYVehicle) {
          return BadgeTypes.SevereGreenRWY;
        }
        return BadgeTypes.SevereGreen;
      }
      if (isRWYVehicle) {
        return BadgeTypes.SevereRedRWY;
      }
      return BadgeTypes.SevereRed;
    }
  };

  const getColorByIssueType = (badgeType: BadgeTypes | undefined) => {
    if (
      badgeType === BadgeTypes.WarningGreenRWY ||
      badgeType === BadgeTypes.AlertGreenRWY ||
      badgeType === BadgeTypes.SevereGreenRWY
    ) {
      return '#1C7903';
    }
    if (badgeType === BadgeTypes.AlertOrangeRWY) {
      return '#DE8D0D';
    }
    if (badgeType === BadgeTypes.SevereRedRWY) {
      return '#C92828';
    }
  };
  const handleRunwayClick = (runway: ISelectOption<number>) => {
    dispatch(actions.load.request({ runwayId: runway.value }));

    setSelectedRunway(selectedRunway?.value === runway.value ? null : runway);
  };

  useEffect(() => {
    if (selectedRunway != null) {
      dispatch(widgetsActions.update({ domain: props.reduxKey, uid: props.uid, data: { selectedRunway } }));
    }
  }, [selectedRunway]);

  const navigateToClosedRunwayIssues = () => {
    history.push(`${AllRoutes.Issues}?tab=${RuleGroup.CLOSED_RUNWAY}`);
  };

  const navigateToOpenedRunwayIssues = () => {
    history.push(`${AllRoutes.Issues}?tab=${RuleGroup.OPEN_RUNWAY}`);
  };

  useEffect(() => {
    dispatch(actions.subscribe());
    dispatch(runwayActions.load.request());
    if (widgets[props.reduxKey].map[props.uid]?.data?.selectedRunway) {
      setSelectedRunway(widgets[props.reduxKey].map[props.uid].data.selectedRunway);
      dispatch(actions.load.request({ runwayId: widgets[props.reduxKey].map[props.uid].data.selectedRunway.value }));
    }

    return () => {
      dispatch(actions.unsubscribe());
    };
  }, []);

  useEffect(() => {
    props.update();
  }, [digest]);

  const getTitle = useCallback(() => (selectedRunway ? selectedRunway.label : fc('Runway info')), [selectedRunway]);
  const getDevicesList = useCallback(
    () =>
      selectedRunway && (
        <>
          <>
            {digest.digests && digest.digests[selectedRunway.value]?.guarded?.length ? (
              <>
                <DevicesList>
                  {digest.digests[selectedRunway.value]?.guarded.map((device) => getDeviceListItem(device))}
                </DevicesList>
                <div
                  style={{
                    fontSize: '8px',
                    color: '##474E5A',
                    paddingLeft: '14px',
                    fontWeight: 'bold',
                    paddingTop: '4px',
                    paddingBottom: '4px',
                    borderBottom: '1px solid #E5E5EA',
                    borderLeft: '1px solid #E5E5EA',
                    borderRight: '1px solid #E5E5EA',
                  }}
                >
                  No. of vehicles: {digest.digests[selectedRunway.value]?.guarded?.length || 0}
                </div>
              </>
            ) : (
              <NoDataView />
            )}
          </>
          {selectedRunway &&
            digest.digests &&
            digest.digests[selectedRunway.value] &&
            digest.digests[selectedRunway.value].runwayOpen && (
              <div style={{ border: '1px solid #E5E5EA', marginTop: '10px', marginBottom: '10px' }}>
                <div
                  style={{
                    justifyContent: 'space-around',
                    margin: 0,
                    fontSize: '10px',
                    display: 'flex',
                    minWidth: '100%',
                    paddingBottom: '6px',
                    paddingTop: '6px',
                    borderBottom: '1px solid #E5E5EA',
                  }}
                >
                  <div
                    style={{
                      width: '60px',
                      display: 'flex',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      color: '#000000',
                    }}
                  >
                    Incursion
                  </div>
                  <div
                    style={{
                      width: '60px',
                      display: 'flex',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      color: '#C92828',
                    }}
                  >
                    Inner
                  </div>
                  <div
                    style={{
                      width: '60px',
                      display: 'flex',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      color: '#DE8D0D',
                    }}
                  >
                    Outer
                  </div>
                </div>
                <div
                  style={{
                    fontSize: '10px',
                    color: '#33558B',
                    marginTop: '10px',
                    marginLeft: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  No. of vehicles
                </div>
                <RunwayCards>
                  <OpenedRunwayCard onClick={navigateToOpenedRunwayIssues}>
                    <CardCounter color={'#000000'}>
                      {digest.digests &&
                        Number(digest.digests[selectedRunway.value]?.incursionZone?.permitted) +
                          Number(digest.digests[selectedRunway.value]?.incursionZone?.unresolved) +
                          Number(digest.digests[selectedRunway.value]?.incursionZone?.forbidden)}
                    </CardCounter>
                  </OpenedRunwayCard>
                  <OpenedRunwayCard onClick={navigateToOpenedRunwayIssues}>
                    <CardCounter color={'#C92828'}>
                      {digest.digests &&
                        Number(digest.digests[selectedRunway.value]?.innerZone?.permitted) +
                          Number(digest.digests[selectedRunway.value]?.innerZone?.unresolved) +
                          Number(digest.digests[selectedRunway.value]?.innerZone?.forbidden)}
                    </CardCounter>
                  </OpenedRunwayCard>
                  <OpenedRunwayCard onClick={navigateToOpenedRunwayIssues}>
                    <CardCounter color={'#DE8D0D'}>
                      {(digest.digests &&
                        Number(digest.digests[selectedRunway.value]?.outerZone?.permitted) +
                          Number(digest.digests[selectedRunway.value]?.outerZone?.unresolved) +
                          Number(digest.digests[selectedRunway.value]?.outerZone?.forbidden)) ||
                        0}
                    </CardCounter>
                  </OpenedRunwayCard>
                </RunwayCards>
                <div
                  style={{
                    fontSize: '10px',
                    color: '#33558B',
                    marginLeft: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  Unresolved Alerts
                </div>
                <RunwayCards>
                  <OpenedRunwayCard onClick={navigateToOpenedRunwayIssues}>
                    <CardCounter color={'#000000'}>
                      {(digest.digests && digest.digests[selectedRunway.value]?.incursionZone?.unresolved) || 0}
                    </CardCounter>
                  </OpenedRunwayCard>
                  <OpenedRunwayCard onClick={navigateToOpenedRunwayIssues}>
                    <CardCounter color={'#C92828'}>
                      {(digest.digests && digest.digests[selectedRunway.value]?.innerZone?.unresolved) || 0}
                    </CardCounter>
                  </OpenedRunwayCard>
                  <OpenedRunwayCard onClick={navigateToOpenedRunwayIssues}>
                    <CardCounter color={'#DE8D0D'}>
                      {(digest.digests && digest.digests[selectedRunway.value]?.outerZone?.unresolved) || 0}
                    </CardCounter>
                  </OpenedRunwayCard>
                </RunwayCards>
              </div>
            )}
          {selectedRunway &&
            digest.digests &&
            digest.digests[selectedRunway.value] &&
            !digest.digests[selectedRunway.value]?.runwayOpen && (
              <RunwayCards>
                <ClosedRunwayCard onClick={navigateToClosedRunwayIssues}>
                  <CardIndicator color={'#005095'}>Total No. Of Vehicles In Runway</CardIndicator>
                  <CardCounter color={'#005095'}>
                    {(digest.digests && digest.digests[selectedRunway.value]?.guardZone?.present) || 0}
                  </CardCounter>
                </ClosedRunwayCard>
                <ClosedRunwayCard onClick={navigateToClosedRunwayIssues}>
                  <CardIndicator color={'#C92828'}>Unauthorised Entry</CardIndicator>
                  <CardCounter color={'#C92828'}>
                    {(digest.digests && digest.digests[selectedRunway.value]?.guardZone?.unauthorizedEntries) || 0}
                  </CardCounter>
                </ClosedRunwayCard>
                <ClosedRunwayCard onClick={navigateToClosedRunwayIssues}>
                  <CardIndicator color={'#C92828'}>Unauthorised Exit</CardIndicator>
                  <CardCounter color={'#C92828'}>
                    {(digest.digests && digest.digests[selectedRunway.value]?.guardZone?.unauthorizedExits) || 0}
                  </CardCounter>
                </ClosedRunwayCard>
              </RunwayCards>
            )}
        </>
      ),
    [selectedRunway, digest],
  );

  useImperativeHandle(props.customRef, () => ({
    title: getTitle,
    configuration: () => {
      return (
        <div style={{ height: 90 }}>
          <RunwaysListStyled>
            Runway
            <Select
              defaultText={'Select runway'}
              selectedValue={selectedRunway}
              options={runwaysItems()}
              onOptionChange={handleRunwayClick}
            />
          </RunwaysListStyled>
        </div>
      );
    },
  }));

  const runwaysItems = () => {
    return runways.list?.map((runway) => ({ value: runway.id, label: runway.name })) || null;
  };

  return (
    <RunwaysListStyled>
      <div>
        {!selectedRunway && (
          <Select
            defaultText={'Select runway'}
            selectedValue={selectedRunway}
            options={runwaysItems()}
            onOptionChange={handleRunwayClick}
          />
        )}
      </div>
      {getDevicesList()}
    </RunwaysListStyled>
  );
};

export default RunwayInfo;
